"use client";

import { observer } from "mobx-react-lite";
import { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  YAxis,
} from "recharts";
import { TrendingUp, BookOpen, DollarSign, Users } from "lucide-react";
import { useAppContext } from "../../../shared/functions/Context";

import { Button } from "../../dashboard/company-dashboard/ui/button";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../dashboard/company-dashboard/ui/chart";
const PersonalDevelopmentDashboard = observer(() => {
  const { api, store, ui } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const allTrainings = store.trainingRequest.all;
  const allBudget = store.trainingBudget.all;
  const allDepartments = store.department.all;

  // Budget calculations
  const budgetData = allBudget.map((training) => training.asJson);
  const internalBudget = budgetData.find(
    (training) => training.type === "Internal"
  );
  const externalBudget = budgetData.find(
    (training) => training.type === "External"
  );

  const internalBudgetTotal = internalBudget?.totalAmount || 0;
  const externalBudgetTotal = externalBudget?.totalAmount || 0;
  const internalBudgetAvailable = internalBudget?.available || 0;
  const externalBudgetAvailable = externalBudget?.available || 0;

  // Profile calculations
  const allProfiles = store.personalDevelopment.all.map(
    (profile) => profile.asJson
  );
  const maleCount = allProfiles.filter(
    (profile) => profile.gender === "Male"
  ).length;
  const femaleCount = allProfiles.filter(
    (profile) => profile.gender === "Female"
  ).length;

  // Training calculations
  const trainingData = allTrainings.map((training) => training.asJson);
  const TotalTrainings = trainingData.length;
  const completedEternalTrainings = trainingData.filter(
    (training) =>
      training.trainingType === "External" && training.status === "Completed"
  ).length;
  const ExternalTrainings = trainingData.filter(
    (training) => training.trainingType === "External"
  ).length;
  const InternalTrainings = trainingData.filter(
    (training) => training.trainingType === "Internal"
  ).length;

  const internalCompleted = trainingData.filter(
    (training) =>
      training.trainingType === "Internal" && training.status === "Completed"
  ).length;
  const completedPercentage = InternalTrainings
    ? Number.parseFloat(
        ((internalCompleted / InternalTrainings) * 100).toFixed(1)
      )
    : 0.0;
  const completedExternalPercentage = ExternalTrainings
    ? Number.parseFloat(
        ((completedEternalTrainings / ExternalTrainings) * 100).toFixed(1)
      )
    : 0.0;

  const totalCompleted = completedEternalTrainings + internalCompleted;
  const overallCompletionPercentage = TotalTrainings
    ? Number.parseFloat(((totalCompleted / TotalTrainings) * 100).toFixed(1))
    : 0.0;

  // Chart data
  const departmentChartData = allDepartments.map((department) => ({
    name: department.asJson.name,
    trainings: trainingData.filter(
      (training) => training.department === department.asJson.id
    ).length,
  }));

  const genderChartData = [
    { name: "Male", value: maleCount, fill: "hsl(var(--chart-1))" },
    { name: "Female", value: femaleCount, fill: "hsl(var(--chart-2))" },
  ];

  const budgetChartData = [
    {
      name: "Internal",
      total: internalBudgetTotal,
      available: internalBudgetAvailable,
      spent: internalBudgetTotal - internalBudgetAvailable,
    },
    {
      name: "External",
      total: externalBudgetTotal,
      available: externalBudgetAvailable,
      spent: externalBudgetTotal - externalBudgetAvailable,
    },
  ];

  const chartConfig = {
    trainings: {
      label: "Trainings",
      color: "#84cbe9",
    },
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        await Promise.all([
          api.personalDevelopment.getAll(),
          api.department.getAll(),
          api.user.getAll(),
          api.trainingRequest.getAll(),
          api.trainingBudget.getAll(),
        ]);
      } catch (error) {
        ui.snackbar.load({
          id: Date.now(),
          message: "Failed to load.",
          type: "danger",
        });
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [api, ui.snackbar]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#84cbe9]"></div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#3f6a8e]">
            Training Dashboard
          </h1>
          <p className="text-gray-600 mt-1">
            Overview of all training activities and statistics
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <BookOpen className="w-6 h-6 text-[#84cbe9]" />
            </div>
            <span className="text-sm font-medium text-gray-500">Total</span>
          </div>
          <h3 className="text-3xl font-bold text-[#3f6a8e] mb-1">
            {TotalTrainings}
          </h3>
          <p className="text-sm text-gray-600">All training programs</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-[#4bb543]" />
            </div>
            <span className="text-sm font-medium text-gray-500">
              Completion
            </span>
          </div>
          <h3 className="text-3xl font-bold text-[#4bb543] mb-1">
            {overallCompletionPercentage}%
          </h3>
          <p className="text-sm text-gray-600">
            {totalCompleted} of {TotalTrainings} completed
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <BookOpen className="w-6 h-6 text-[#e7a637]" />
            </div>
            <span className="text-sm font-medium text-gray-500">External</span>
          </div>
          <h3 className="text-3xl font-bold text-[#e7a637] mb-1">
            {ExternalTrainings}
          </h3>
          <p className="text-sm text-gray-600">
            {completedExternalPercentage}% completed
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <BookOpen className="w-6 h-6 text-[#6f42c1]" />
            </div>
            <span className="text-sm font-medium text-gray-500">Internal</span>
          </div>
          <h3 className="text-3xl font-bold text-[#6f42c1] mb-1">
            {InternalTrainings}
          </h3>
          <p className="text-sm text-gray-600">
            {completedPercentage}% completed
          </p>
        </div>
      </div>

      {showMore && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-in fade-in duration-300">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-500">Pending</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {TotalTrainings - totalCompleted}
            </h3>
            <p className="text-xs text-gray-600 mt-1">Trainings in progress</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-500">
                Internal Done
              </span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {internalCompleted}
            </h3>
            <p className="text-xs text-gray-600 mt-1">Completed internal</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-500">
                External Done
              </span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {completedEternalTrainings}
            </h3>
            <p className="text-xs text-gray-600 mt-1">Completed external</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-500">
                  Participants
                </span>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {allProfiles.length}
            </h3>
            <p className="text-xs text-gray-600 mt-1">Total enrolled</p>
          </div>
        </div>
      )}

      <div className="flex justify-center">
        <Button
          onClick={() => setShowMore(!showMore)}
          className="border-2 border-[#84cbe9] bg-white text-[#84cbe9] hover:bg-blue-50"
        >
          {showMore ? "View Less" : "View More Statistics"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-[#3f6a8e] mb-6">
            Trainings by Department
          </h2>
          <ChartContainer config={chartConfig} className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={departmentChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#6b7280", fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fill: "#6b7280", fontSize: 12 }} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="trainings" fill="#84cbe9" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-[#3f6a8e] mb-6">
            Gender Distribution
          </h2>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={genderChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {genderChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
          <h2 className="text-xl font-bold text-[#3f6a8e] mb-6">
            Budget Overview
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={budgetChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" tick={{ fill: "#6b7280" }} />
              <YAxis tick={{ fill: "#6b7280" }} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="total"
                fill="#84cbe9"
                radius={[8, 8, 0, 0]}
                name="Total Budget"
              />
              <Bar
                dataKey="spent"
                fill="#e7a637"
                radius={[8, 8, 0, 0]}
                name="Spent"
              />
              <Bar
                dataKey="available"
                fill="#4bb543"
                radius={[8, 8, 0, 0]}
                name="Available"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-blue-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-[#84cbe9]" />
            </div>
            <h2 className="text-xl font-bold text-[#3f6a8e]">
              Internal Budget
            </h2>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Budget:</span>
              <span className="text-lg font-bold text-gray-900">
                ${internalBudgetTotal.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Available:</span>
              <span className="text-lg font-bold text-[#4bb543]">
                ${internalBudgetAvailable.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Spent:</span>
              <span className="text-lg font-bold text-[#e7a637]">
                $
                {(
                  internalBudgetTotal - internalBudgetAvailable
                ).toLocaleString()}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden relative">
              <div
                className="bg-[#84cbe9] h-3 rounded-full transition-all duration-500"
                style={{
                  width: `${Math.min(
                    100,
                    internalBudgetTotal
                      ? ((internalBudgetTotal - internalBudgetAvailable) /
                          internalBudgetTotal) *
                          100
                      : 0
                  )}%`,
                }}
              ></div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-[#e7a637]" />
            </div>
            <h2 className="text-xl font-bold text-[#3f6a8e]">
              External Budget
            </h2>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Budget:</span>
              <span className="text-lg font-bold text-gray-900">
                ${externalBudgetTotal.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Available:</span>
              <span className="text-lg font-bold text-[#4bb543]">
                ${externalBudgetAvailable.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Spent:</span>
              <span className="text-lg font-bold text-[#e7a637]">
                $
                {(
                  externalBudgetTotal - externalBudgetAvailable
                ).toLocaleString()}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden relative">
              <div
                className="bg-[#e7a637] h-3 rounded-full transition-all duration-500"
                style={{
                  width: `${Math.min(
                    100,
                    externalBudgetTotal
                      ? ((externalBudgetTotal - externalBudgetAvailable) /
                          externalBudgetTotal) *
                          100
                      : 0
                  )}%`,
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PersonalDevelopmentDashboard;
